import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { Session } from '@supabase/supabase-js';

const SESSION_TIMEOUT_MS = 8000;

/** Race a promise against a timeout - prevents auth from hanging when Supabase is slow (e.g. disk IO throttling) */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    ),
  ]);
}

/**
 * Centralized session query with React Query caching
 * This prevents duplicate session checks
 *
 * PERFORMANCE: Uses optimistic initial data to reduce perceived load time
 */
export function useSessionQuery() {
  return useQuery<Session | null>({
    queryKey: ['session'],
    queryFn: async () => {
      const startTime = performance.now();
      const { data: { session }, error } = await withTimeout(
        supabase.auth.getSession(),
        SESSION_TIMEOUT_MS,
        'Session fetch'
      );

      if (error) {
        logger.error('Error fetching session', { error });

        // Check if it's a network error vs auth error
        if (error.message?.includes('network') || error.message?.includes('fetch')) {
          logger.warn('Network error during session fetch, may be offline', { error });
        }

        throw error;
      }

      // Validate session integrity if one exists
      if (session) {
        const now = Date.now();
        const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;

        // Check if session is expired or about to expire (within 5 minutes)
        if (expiresAt && expiresAt - now < 5 * 60 * 1000) {
          logger.warn('Session is expired or about to expire', {
            expiresAt: new Date(expiresAt).toISOString(),
            timeUntilExpiry: Math.floor((expiresAt - now) / 1000 / 60) + ' minutes'
          });
        }

        // Validate user object
        if (!session.user?.id || !session.user?.email) {
          logger.error('Invalid session: missing user data', {
            hasId: !!session.user?.id,
            hasEmail: !!session.user?.email
          });
        }
      }

      // Log timing in development for performance monitoring
      if (import.meta.env.DEV) {
        const elapsed = performance.now() - startTime;
        if (elapsed > 500) {
          logger.warn('Session fetch slow', { elapsed: `${elapsed.toFixed(0)}ms` });
        }
      }

      return session;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false, // Prevent refetch on focus to reduce flickering
    refetchOnMount: true, // Only refetch if stale, to prevent excessive session checks
    refetchOnReconnect: true,
    // PERFORMANCE: Don't show loading state while refetching in background
    placeholderData: (previousData) => previousData,
    // PERFORMANCE: Retry only once for faster failure recovery
    retry: 1,
    retryDelay: 500,
    // PERFORMANCE: Reduce network timeout for faster fallback
    networkMode: 'offlineFirst',
  });
}
